/**
 * MetricCard — Reusable card component for displaying a single financial metric.
 * Shows value, label, variance percentage, and performance color.
 * Clean white design with subtle borders — highlighted variant uses WMP blue accent.
 */

import { CalcTooltip } from "./CalcTooltip";
import { SERIES } from "../utils/chartTheme";

interface MetricCardProps {
  label: string;
  value: string;
  variance?: string;
  varianceLabel?: string;
  color?: string; // hex color for value
  highlighted?: boolean; // accent style
  /** Formula description for rich tooltip (line 1) */
  formula?: string;
  /** Substituted values for rich tooltip (line 2) */
  formulaValues?: string;
}

export function MetricCard({
  label,
  value,
  variance,
  varianceLabel,
  color,
  highlighted = false,
  formula,
  formulaValues,
}: MetricCardProps) {
  const hasTooltip = !!(formula && formulaValues);

  if (highlighted) {
    const card = (
      <div
        className={`bg-white border border-wmp-500/25 rounded-xl shadow-sm p-5 text-center relative overflow-hidden${hasTooltip ? " cursor-help" : ""}`}
      >
        {/* Subtle blue accent bar at top */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-wmp-500" />
        <p className="text-2xs font-semibold tracking-wider text-wmp-600 uppercase">
          {label}
        </p>
        <p className="text-2xl font-bold tracking-tight tabular-nums text-gray-900 mt-2">
          {value}
        </p>
        {variance && (
          <p
            className="text-sm font-medium tabular-nums mt-1"
            style={{ color: color || SERIES.primary }}
          >
            {variance}
            {varianceLabel && (
              <span className="text-gray-400 text-xs ml-1">
                {varianceLabel}
              </span>
            )}
          </p>
        )}
      </div>
    );

    if (hasTooltip) {
      return (
        <CalcTooltip formula={formula} values={formulaValues}>
          {card}
        </CalcTooltip>
      );
    }
    return card;
  }

  const card = (
    <div
      className={`bg-white border border-gray-200/70 rounded-xl shadow-sm p-5 text-center${hasTooltip ? " cursor-help" : ""}`}
    >
      <p className="microlabel">{label}</p>
      <p
        className="text-2xl font-bold tracking-tight tabular-nums mt-2"
        style={{ color: color || "#1e293b" }}
      >
        {value}
      </p>
      {variance && (
        <p className="text-sm font-medium tabular-nums mt-1 text-gray-500">
          {variance}
          {varianceLabel && (
            <span className="text-gray-400 text-xs ml-1">{varianceLabel}</span>
          )}
        </p>
      )}
    </div>
  );

  if (hasTooltip) {
    return (
      <CalcTooltip formula={formula} values={formulaValues}>
        {card}
      </CalcTooltip>
    );
  }
  return card;
}
